import React, { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import { Box, Flex } from 'rebass';
import { useHistory } from 'react-router-dom';
import useMode from '../../../../hooks/useMode';
import useNotify from '../../../../hooks/useNotify';
import ThirdPaneLayout, {
  MainColumn,
} from '../../../../components/Layout/ThirdPaneLayout';
import ThirdPane from '../../../../components/ThirdPane';
import ThirdPaneHeader from '../../../../components/ThirdPane/ThirdPaneHeader';
import ThirdPaneContent from '../../../../components/ThirdPane/ThirdPaneContent';
import ThirdPaneFooter from '../../../../components/ThirdPane/ThirdPaneFooter';
import Button from '../../../../components/Button/Button';
import SurveyTemplateForm, {
  SurveyTemplateInput,
} from '../../../../components/WaterHazard/SurveyTemplateForm';
import HazardSurveyResult from '../../../../components/WaterHazard/HazardSurveyResult';
import {
  CREATE_SURVEY_TEMPLATE,
  GET_SURVEY_TEMPLATES,
  SurveyTemplateItem,
  UPDATE_SURVEY_TEMPLATE,
} from './SurveyTemplates.query';
import { GET_WATER_HAZARDS } from '../waterHazards/WaterHazards.query';
import { ApolloError } from '@apollo/client/errors';

type SurveyTemplateEditorProps = RouteComponentProps<{ id: string }>;

function SurveyTemplateEditor({
  match,
}: SurveyTemplateEditorProps): JSX.Element {
  const templateId = match.params.id;
  const isCreating = !templateId || templateId === 'add';
  const { showError, showSuccess } = useNotify();
  const mode = useMode();
  const history = useHistory();

  const [template, setTemplate] = useState<SurveyTemplateInput>({
    name: '',
    description: '',
    instructions: '',
    otherHazardsQuestion: false,
    protectedQuestion: false,
    hazardIds: [],
  });

  const { data: existingData, loading: itemLoading } = useQuery(
    GET_SURVEY_TEMPLATES,
    {
      variables: {
        where: { id: templateId },
      },
      skip: isCreating,
    }
  );

  useEffect(() => {
    if (existingData?.waterHazardSurveyTemplates?.[0]) {
      const existing = existingData
        .waterHazardSurveyTemplates[0] as SurveyTemplateItem;
      setTemplate({
        name: existing.name,
        description: existing.description,
        instructions: existing.instructions || '',
        otherHazardsQuestion: !!existing.otherHazardsQuestion,
        protectedQuestion: !!existing.protectedQuestion,
        hazardIds: existing.items.map((i) => i.waterHazardId),
      });
    }
  }, [existingData]);

  const { data: hazardsData } = useQuery(GET_WATER_HAZARDS);

  const [saveTemplate, { loading: saveLoading }] = useMutation<any, any>(
    isCreating ? CREATE_SURVEY_TEMPLATE : UPDATE_SURVEY_TEMPLATE,
    {
      onCompleted: () => {
        showSuccess('Survey template saved');
        history.push(`/${mode}/survey-templates`);
      },
      onError: (error: ApolloError) => {
        showError('Error saving survey template', error);
      },
    }
  );

  const handleSave = useCallback(async () => {
    if (!template.name) {
      showError('Please enter a name');
      return;
    }

    if (isCreating) {
      await saveTemplate({
        variables: {
          input: template,
        },
      });
    } else {
      await saveTemplate({
        variables: {
          templateId,
          input: template,
        },
      });
    }
  }, [template, templateId, saveTemplate, isCreating, showError]);

  const handleCancel = () => {
    history.push(`/${mode}/survey-templates`);
  };

  return (
    <ThirdPaneLayout paneVisible={true}>
      <MainColumn width="calc(100% - 600px)">
        <Flex flexDirection="column" height="100%">
          <Box p={3} bg="gray.100" flexGrow={1} sx={{ overflowY: 'auto' }}>
            <HazardSurveyResult
              key={`${template.protectedQuestion}-${template.otherHazardsQuestion}`}
              template={template}
              hazards={hazardsData?.waterHazards ?? []}
            />
          </Box>
        </Flex>
      </MainColumn>
      <ThirdPane width={600} visible={true}>
        <ThirdPaneHeader
          title={isCreating ? 'Add Survey Template' : 'Edit Survey Template'}
        />
        <ThirdPaneContent p={0}>
          {itemLoading ? (
            <Flex justifyContent="center" alignItems="center" flexGrow={1}>
              Loading...
            </Flex>
          ) : (
            <SurveyTemplateForm template={template} onChange={setTemplate} />
          )}
        </ThirdPaneContent>
        <ThirdPaneFooter justifyContent="space-between">
          <Button
            variant="primary"
            icon="save"
            onClick={handleSave}
            disabled={saveLoading}
          >
            {saveLoading ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button variant="outline" icon="slash-circle" onClick={handleCancel}>
            Cancel
          </Button>
        </ThirdPaneFooter>
      </ThirdPane>
    </ThirdPaneLayout>
  );
}

export default withRouter(SurveyTemplateEditor);
